import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Divider, Stack, Text, Accordion, StackProps } from "@bookingcom/bui-react";
import type { UIUnifiedPriceItem } from "@flights/types";
import useFormatPrice from "../../../../hooks/useFormatPrice";

import {
  useBreakdownController,
  BreakdownControllerProps,
  type BreakdownScopeId,
  type BreakdownScopeItems,
  type BreakdownScope
} from "./useBreakdown.controller";
import styles from "./Breakdown.module.css";
import {
  usePriceConsistencyMonitoringEvents,
  type PriceRenderedProductPrice
} from "../../../../hooks/c360/usePriceConsistencyMonitoringEvents";
import { isFeatureRunningClientSide } from "@flights/web-api-utils-universal";
import { getAttachedAncillaries } from "../helpers";
import { actions as ariaLiveActions } from "../../../../store/ariaLive/actions";
import { t } from "@bookingcom/lingojs-core";
import { useDispatch } from "react-redux";
import { useI18n } from "@bookingcom/lingojs-react";
import usePrevious from "@bookingcom/bui-react/hooks/usePrevious";

const extraOrder = ["flexibleticket", "cabinbag", "checkedbag", "travelinsurance", "cfar", "seats"];
const flightOrder = ["adult", "child", "infant"];

function sortItems(sortArr: string[], items: UIUnifiedPriceItem[]) {
  return items.sort((a, b) => {
    const idxA = sortArr.findIndex((v) => a.id.includes(v));
    const idxB = sortArr.findIndex((v) => b.id.includes(v));
    if (idxA === -1) return 1;
    if (idxB === -1) return -1;
    return idxA - idxB;
  });
}

function sortSectionItems(id: BreakdownScopeId, items: UIUnifiedPriceItem[]) {
  if (id === "EXTRA") {
    return sortItems(extraOrder, items);
  } else if (id === "FLIGHT") {
    return sortItems(flightOrder, items);
  } else {
    return items.sort((a, b) => a.title.localeCompare(b.title));
  }
}

function SectionItems({ items, mixin }: { items: BreakdownScopeItems; mixin?: StackProps["mixin"] }) {
  const { formatPrice } = useFormatPrice();
  return useMemo(
    () =>
      items.map(({ id, items, price, title }, idx) => (
        <Stack mixin={mixin} gap={1} key={`${id}-${idx}`}>
          <Stack gap={2} direction="row">
            <Stack.Item grow>
              <Text variant="small_1" color="neutral" attributes={{ "data-testid": `upb_title-${id}` }}>
                {title}
              </Text>
            </Stack.Item>
            <Text
              variant="small_1"
              color={price.units < 0 || (price.units === 0 && price.nanos < 0) ? "constructive" : "neutral"}
              attributes={{ "data-testid": `upb_price-${id}` }}
            >
              {formatPrice(price)}
            </Text>
          </Stack>
          {items.length ? <SectionItems mixin={{ paddingInlineStart: 2 }} items={items} /> : undefined}
        </Stack>
      )),
    [items, mixin, formatPrice]
  );
}

function AccordionItem({ item, expandedByDefault }: { item: UIUnifiedPriceItem; expandedByDefault?: boolean }) {
  const { formatPrice } = useFormatPrice();
  const [active, setActive] = useState(expandedByDefault);

  return (
    <Accordion
      active={active}
      className={styles.accordion}
      onClose={() => {
        setActive(false);
      }}
      onOpen={() => {
        setActive(true);
      }}
      titleContent={
        <Stack gap={0} direction="row">
          <Stack.Item grow>
            <Text variant="body_2" attributes={{ "data-testid": `upb_title-${item.id}` }}>
              {item.title}
            </Text>
          </Stack.Item>
          <Text
            variant="body_2"
            color={
              item.price.units < 0 || (item.price.units === 0 && item.price.nanos < 0) ? "constructive" : "neutral"
            }
            attributes={{ "data-testid": `upb_price-${item.id}` }}
          >
            {formatPrice(item.price)}
          </Text>
        </Stack>
      }
    >
      {item.items.length ? (
        <Stack gap={1} mixin={{ paddingBlockStart: 2, paddingBlockEnd: 1 }}>
          <SectionItems mixin={{ paddingInlineEnd: 10 }} items={item.items} />
        </Stack>
      ) : undefined}
    </Accordion>
  );
}

function AccordionGroup({
  section,
  sectionFooter,
  isLast
}: {
  section: BreakdownScope;
  sectionFooter?: React.ReactNode;
  isLast: boolean;
}) {
  return (
    <>
      <Stack gap={2}>
        <Text variant="strong_2" tagName="h3" attributes={{ "data-testid": `breakdown_section_${section.id}` }}>
          {section.name}
        </Text>
        {useMemo(
          () =>
            sortSectionItems(section.id, section.items).map((item, idx) => (
              <AccordionItem item={item} key={`${item.id}-${idx}`} expandedByDefault={section.expandedByDefault} />
            )),
          [section.items, section.id, section.expandedByDefault]
        )}
      </Stack>
      {sectionFooter !== undefined ? <Stack.Item>{sectionFooter}</Stack.Item> : undefined}
      {!isLast ? <Divider /> : null}
    </>
  );
}

export type BreakdownProps = BreakdownControllerProps;

export function Breakdown(props: BreakdownProps) {
  const {
    totalPrice,
    totalPriceTitle,
    footerSections = {},
    sections = {},
    totalPriceFooter,
    isSummary
  } = useBreakdownController(props);
  const i18n = useI18n();
  const { id: productId, price, items, addedItems } = props;
  const { formatPrice } = useFormatPrice();
  const trackPriceConsistencyMonitoringEvents = usePriceConsistencyMonitoringEvents();
  const dispatch = useDispatch();

  const sortedIds: BreakdownScopeId[] = useMemo(() => ["FLIGHT", "EXTRA", "BOOKING_CHANGES", "FEE", "DISCOUNT"], []);
  const sortedSections = useMemo<BreakdownScope[]>(
    () => sortedIds.map((id) => sections?.[id]).filter((section): section is BreakdownScope => Boolean(section)),
    [sections, sortedIds]
  );

  const formattedTotalPrice = formatPrice(totalPrice);
  const prevFormattedTotalPrice = usePrevious(formattedTotalPrice);

  // Announce price changes to screen readers
  useEffect(() => {
    if (formattedTotalPrice !== prevFormattedTotalPrice) {
      dispatch(
        ariaLiveActions.setMessage({
          message: i18n.trans(
            t("a11y_flights_aria_live_checkout_new_total_price", {
              variables: { price: formattedTotalPrice }
            })
          ),
          type: "polite"
        })
      );
    }
  }, [dispatch, formattedTotalPrice, i18n, prevFormattedTotalPrice]);

  const accordionItems = useMemo(
    () =>
      sortedSections.map((section, idx) => (
        <AccordionGroup
          key={`${section.id}-${idx}`}
          section={section}
          sectionFooter={footerSections?.[section.id]}
          isLast={idx === sortedSections.length - 1}
        />
      )),
    [sortedSections, footerSections]
  );

  const flattenUnifiedPriceItems = useCallback(
    (items: UIUnifiedPriceItem[]): PriceRenderedProductPrice[] =>
      items.flatMap((item) => [
        { productType: item.id, productPrice: item.price },
        ...flattenUnifiedPriceItems(item.items)
      ]),
    []
  );

  const attachedAncillaries = useMemo(
    () => getAttachedAncillaries({ id: productId, price, items, addedItems }),
    [productId, price, items, addedItems]
  );

  useEffect(() => {
    if (
      isFeatureRunningClientSide("FLIGHTS_PRICING_ENABLE_SUBMIT_C360_EVENTS_FOR_PRICE_CONSISTENCY") &&
      productId &&
      (items.length || addedItems.length)
    ) {
      const productPrices = flattenUnifiedPriceItems([...items, ...addedItems]);

      trackPriceConsistencyMonitoringEvents("BREAKDOWN", productId, productPrices, totalPrice, attachedAncillaries);
    }
  }, [
    addedItems,
    flattenUnifiedPriceItems,
    items,
    productId,
    totalPrice,
    trackPriceConsistencyMonitoringEvents,
    attachedAncillaries
  ]);

  return (
    <Stack direction="column" gap={2} attributes={{ "data-testid": "upb_container" }}>
      {!isSummary && (
        <>
          <Stack gap={2}>{accordionItems}</Stack>
          <Divider />
        </>
      )}
      <Stack gap={0} mixin={!isSummary ? { paddingInlineEnd: 10 } : {}}>
        <Stack direction="row" gap={0}>
          <Stack.Item grow>
            <Text
              variant={!isSummary ? "headline_3" : "strong_1"}
              tagName="h2"
              attributes={{ "data-testid": "upb_title-total" }}
            >
              {totalPriceTitle}
            </Text>
          </Stack.Item>
          <Stack direction="column" gap={0}>
            <Text variant={!isSummary ? "headline_3" : "strong_1"} attributes={{ "data-testid": "upb_price-total" }}>
              {formattedTotalPrice}
            </Text>
          </Stack>
        </Stack>
        <Text variant="body_2" className={styles.totalPriceFooter}>
          {totalPriceFooter}
        </Text>
      </Stack>
    </Stack>
  );
}
